import { useCallback, useEffect } from "react";
import { fetchInboxPoll, isHumanAgentInboxLive } from "@/api/inboxAgentApi";
import { workspaceHumanAgentSettingsClient } from "@/api/workspaceHumanAgentSettingsClient";
import { mapNotificationsFromApi } from "@/api/workspaceHumanAgentSettingsMappers";
import {
  bootstrapInboxAlerts,
  dispatchInboxPollEvents,
  setInboxAlertPrefs,
} from "@/lib/inboxMessageAlerts";
import { notifyInboxPollListEvents } from "@/lib/inboxPollListEvents";
import { filterUnseenPollEvents } from "@/lib/inboxPollEventSeen";

const POLL_INTERVAL_MS = 8000;
const INITIAL_LOOKBACK_MS = 30_000;
/**
 * Rewind applied to the server cursor on every tick. A row can be stamped before
 * the poll's snapshot yet commit after it; without this overlap that event would
 * fall behind the next cursor and never be reported, leaving a new chat invisible
 * until the slow full list refresh. Re-delivered events are filtered by id.
 */
const CURSOR_OVERLAP_MS = 15_000;

/**
 * App-wide inbox alerts hook (lives in ProtectedLayout — persists across page navigations).
 *
 * Polls GET /api/v1/agent/inbox/poll every 8 s in central scope as the backstop for
 * the tenant-wide `user_message` socket event handled in AgentPresenceContext:
 * whichever path sees a message first alerts, the other is de-duped. The poll
 * keeps running while the tab is hidden so a backgrounded or minimized window
 * still alerts, though browsers throttle background timers, so the socket is
 * what makes hidden-tab alerts prompt. The server limits central polls to the
 * workspaces in the forwarded access context.
 *
 * @param {{ workspaceId?: string | null }} opts
 */
export function useInboxMessageAlerts({ workspaceId = null } = {}) {
  const loadPrefs = useCallback(async () => {
    try {
      const raw = await workspaceHumanAgentSettingsClient.getNotifications();
      const mapped = mapNotificationsFromApi(raw);
      setInboxAlertPrefs({
        desktopEnabled: mapped.desktopNotif !== false,
        soundEnabled: mapped.soundNotif !== false,
        inAppEnabled: true,
      });
    } catch {
      setInboxAlertPrefs({ desktopEnabled: true, soundEnabled: true, inAppEnabled: true });
      console.warn("[inbox-alerts]", {
        operation: "preferences_load",
        source: "settings",
        retryable: true,
      });
    }
  }, []);

  useEffect(() => {
    // Bootstrap: request notification permission + arm audio on mount
    bootstrapInboxAlerts();

    // Poll is the fallback when Socket.IO is down. VITE_REALTIME_DISABLED only
    // skips the socket (AgentPresenceContext); alerts still need this poller.
    if (!isHumanAgentInboxLive()) return undefined;

    void loadPrefs();

    // lastPollMs starts 30 s in the past so the first poll catches recent handoffs.
    const lastPollMsRef = { current: Date.now() - INITIAL_LOOKBACK_MS };
    let inFlight = false;

    const poll = async () => {
      if (inFlight) return;
      inFlight = true;
      try {
        const data = await fetchInboxPoll({
          sinceMs: lastPollMsRef.current,
          workspaceId,
          scope: "central",
        });

        // Advance to just before the server's clock, keeping a short overlap so a
        // late commit cannot slip behind the cursor.
        lastPollMsRef.current = data.server_time_ms - CURSOR_OVERLAP_MS;

        const fresh = filterUnseenPollEvents({
          messages: data.messages ?? [],
          handoffs: data.handoffs ?? [],
        });

        dispatchInboxPollEvents(fresh);
        // Alert dispatch may consume a triggering message, but the untouched
        // fresh payload must still update the conversation list.
        notifyInboxPollListEvents(fresh);
      } catch {
        console.warn("[inbox-alerts]", {
          operation: "poll",
          source: "http",
          retryable: true,
        });
      } finally {
        inFlight = false;
      }
    };

    const timerId = setInterval(poll, POLL_INTERVAL_MS);
    void poll();
    // Catch up immediately on return to the tab, since background timers may
    // have been throttled to once a minute while hidden.
    document.addEventListener("visibilitychange", poll);

    return () => {
      clearInterval(timerId);
      document.removeEventListener("visibilitychange", poll);
    };
  }, [loadPrefs, workspaceId]); // re-starts if workspace changes
}
